<template>
  <section
    ref="cameraPanelRef"
    role="dialog"
    :aria-label="t('whatsapp.file_upload.take_photo')"
    tabindex="-1"
    class="app-card-glass absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden backdrop-blur-2xl rounded-b-xl"
    @keydown.esc.stop="handleCloseCamera"
  >
    <header
      class="flex shrink-0 items-center justify-start gap-2 px-3 py-2 border-t border-[var(--p-surface-100)] dark:border-none"
    >
      <Button
        text
        rounded
        class="min-h-8 min-w-8 flex-shrink-0 !text-[var(--p-surface-500)] dark:!text-[var(--p-primary-100)] rounded-full p-0.5!"
        :aria-label="t('whatsapp.file_upload.cancel')"
        @click="handleCloseCamera"
      >
        <template #icon><X :size="20" /></template>
      </Button>
      <h3
        class="montserrat text-sm text-[var(--p-surface-800)] dark:text-[var(--p-surface-0)] font-medium truncate"
      >
        {{ t('whatsapp.file_upload.take_photo') }}
      </h3>
    </header>
    <div class="flex min-h-0 flex-1 flex-col gap-3 p-3">
      <div
        class="relative min-h-0 flex-1 overflow-hidden rounded-lg bg-transparent flex items-center justify-center"
      >
        <video
          ref="cameraVideoRef"
          autoplay
          playsinline
          muted
          class="h-full w-full object-contain"
          @loadedmetadata="handleCameraReady"
        />

        <div
          v-if="isCameraLoading"
          class="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white"
        >
          {{ t('whatsapp.file_upload.opening_camera') }}
        </div>

        <div
          v-if="cameraError"
          class="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center text-sm text-white"
        >
          {{ cameraError }}
        </div>
      </div>

      <canvas ref="cameraCanvasRef" class="hidden" />

      <PrimeMessage v-if="cameraError" severity="warn" :closable="false">
        {{ t('whatsapp.file_upload.camera_permission_hint') }}
      </PrimeMessage>
    </div>

    <footer class="shrink-0 pb-3">
      <div class="flex w-full justify-center gap-2">
        <!-- <AppButton
          :label="t('whatsapp.file_upload.cancel')"
          severity="secondary"
          outlined
          @click="handleCloseCamera"
        /> -->
        <Button
          v-tooltip.right="t('whatsapp.file_upload.capture')"
          :aria-label="t('whatsapp.file_upload.capture')"
          rounded
          severity="primary"
          :disabled="Boolean(cameraError) || isCameraLoading || !isCameraReady || isCapturingPhoto"
          :loading="isCapturingPhoto"
          class="min-h-13 min-w-13"
          @click="handleCapturePhoto"
        >
          <template #icon>
            <Camera :size="20" class="dark:text-[var(--p-primary-100)]" />
          </template>
        </Button>
      </div>
    </footer>
  </section>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import PrimeMessage from 'primevue/message'
import { Camera, X } from 'lucide-vue-next'
import AppButton from '@/components/atoms/buttons/AppButton.vue'

const emit = defineEmits<{
  capture: [file: File]
  close: []
}>()

const { t } = useI18n()
const cameraPanelRef = ref<HTMLElement | null>(null)
const cameraVideoRef = ref<HTMLVideoElement | null>(null)
const cameraCanvasRef = ref<HTMLCanvasElement | null>(null)
const cameraStream = ref<MediaStream | null>(null)
const cameraError = ref('')
const isCameraLoading = ref(false)
const isCameraReady = ref(false)
const isCapturingPhoto = ref(false)
let isDisposed = false

const stopCamera = () => {
  cameraStream.value?.getTracks().forEach((track) => track.stop())
  cameraStream.value = null
  isCameraReady.value = false

  if (cameraVideoRef.value) {
    cameraVideoRef.value.srcObject = null
  }
}

const startCamera = async () => {
  cameraError.value = ''
  isCameraLoading.value = true
  isCameraReady.value = false
  stopCamera()

  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(t('whatsapp.file_upload.camera_not_supported'))
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    })

    if (isDisposed) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }

    cameraStream.value = stream

    await nextTick()

    const video = cameraVideoRef.value
    if (!video) {
      throw new Error(t('whatsapp.file_upload.camera_view_error'))
    }

    video.srcObject = stream
    await video.play()
  } catch {
    stopCamera()
    cameraError.value = t('whatsapp.file_upload.camera_access_error')
  } finally {
    isCameraLoading.value = false
  }
}

const handleCameraReady = () => {
  const video = cameraVideoRef.value
  isCameraReady.value = Boolean(video && video.videoWidth > 0 && video.videoHeight > 0)
}

const handleCloseCamera = () => {
  stopCamera()
  emit('close')
}

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  })
}

const handleCapturePhoto = async () => {
  const video = cameraVideoRef.value
  const canvas = cameraCanvasRef.value

  if (!video || !canvas || video.videoWidth === 0 || video.videoHeight === 0) {
    cameraError.value = t('whatsapp.file_upload.camera_not_ready')
    return
  }

  isCapturingPhoto.value = true

  try {
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    if (!context) {
      cameraError.value = t('whatsapp.file_upload.photo_processing_error')
      return
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const blob = await canvasToBlob(canvas)
    if (isDisposed) return

    if (!blob) {
      cameraError.value = t('whatsapp.file_upload.image_creation_error')
      return
    }

    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
    stopCamera()
    emit('capture', file)
  } finally {
    isCapturingPhoto.value = false
  }
}

onMounted(() => {
  cameraPanelRef.value?.focus()
  void startCamera()
})

onUnmounted(() => {
  isDisposed = true
  stopCamera()
})
</script>
